
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import WallOfLove from "./pages/WallOfLove";
import Contact from "./pages/Contact";
import BookDemo from "./pages/BookDemo";
import CampaignLanding from "./pages/CampaignLanding";
import SpecialOffer from "./pages/SpecialOffer";
import Launchpad from "./pages/Launchpad";
import LaunchpadThankYou from "./pages/LaunchpadThankYou";
import LaunchpadOffer from "./pages/LaunchpadOffer";
import NotFound from "./pages/NotFound";
import ScrollToTop from "./components/ScrollToTop";
import LovableWidgetManager from "./components/LovableWidgetManager";
import AskSpotWidget from "./components/AskSpotWidget";
import LaunchpadAskSpotWidget from "./components/LaunchpadAskSpotWidget";
import Thanks from "./pages/Thanks";
import GeniusCard from "./pages/GeniusCard";
import GeniusCardUpsell from "./pages/GeniusCardUpsell";
import OTO from "./pages/OTO";
import SpotlightThanks from "./pages/SpotlightThanks";

// Create a new QueryClient with error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onSettled: (data, error) => {
        if (error) {
          console.error('Mutation error:', error);
        }
      }
    }
  }
});

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center p-8 max-w-lg">
            <h2 className="text-2xl font-bold text-[#003a70] mb-4">Something went wrong</h2>
            <p className="mb-6 text-gray-600">We're sorry, but there was an error loading the page.</p>
            <button
              className="bg-[#003a70] text-white px-4 py-2 rounded-lg hover:bg-[#003a70]/90"
              onClick={() => this.setState({ hasError: false })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Component to conditionally render AskSpot widgets
const ConditionalAskSpotWidget = () => {
  const location = useLocation();
  
  // Don't show any widget on these pages
  if (location.pathname === '/genius-card' || location.pathname === '/spotlight-thanks') {
    return null;
  }
  
  // Show the launchpad-specific widget on the launchpad page
  if (location.pathname === '/launchpad') {
    return <LaunchpadAskSpotWidget />;
  }
  
  // Show the regular widget on all other pages
  return <AskSpotWidget />;
};

const App = () => {
  // Add a basic error boundary using useEffect
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error('Global error caught:', event.error);
      // You could add more error handling here if needed
    };

    window.addEventListener('error', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <ScrollToTop />
          <LovableWidgetManager />
          <ConditionalAskSpotWidget />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/wall-of-love" element={<WallOfLove />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/campaign" element={<CampaignLanding />} />
            <Route path="/offer" element={<SpecialOffer />} />
            <Route path="/launchpad" element={<Launchpad />} />
            <Route path="/launchpad/thank-you" element={<LaunchpadThankYou />} />
            <Route path="/launchpad-offer" element={<LaunchpadOffer />} />
            <Route path="/thanks" element={<Thanks />} />
            <Route path="/genius-card" element={<GeniusCard />} />
            <Route path="/genius-card-upsell" element={<GeniusCardUpsell />} />
            <Route path="/oto" element={<OTO />} />
            <Route path="/spotlight-thanks" element={<SpotlightThanks />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
